// History.js - Fixed Version with API Filter Handling
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiClock, FiSearch, FiFilter, FiX, FiChevronDown, FiChevronUp,
  FiCalendar, FiUser, FiTag, FiDollarSign, FiPackage, FiGift,
  FiRefreshCw, FiTrendingUp, FiList, FiGrid, FiInfo,
  FiCheckCircle, FiChevronLeft, FiChevronRight,
  FiFileText, FiBarChart2, FiShield, FiUsers,
  FiGlobe, FiHash, FiLoader, FiCopy, FiCheck, FiEye,
  FiShoppingBag, FiLayers, FiAlertCircle
} from 'react-icons/fi';
import { historyAPI, accountAPI } from '../utils/api';
import toast from 'react-hot-toast';
import useResponsive from '../hooks/useResponsive';

const History = () => {
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [showFilters, setShowFilters] = useState(false);
  const [quickRange, setQuickRange] = useState('today');
  const [userRole, setUserRole] = useState('');
  const filterPanelRef = useRef(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    hasMore: false
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    from_datetime: '',
    to_datetime: '',
    owner_account_id: '',
    status: ''
  });
  
  // Summary filters
  const [summaryFilters, setSummaryFilters] = useState({
    from_datetime: '',
    to_datetime: '',
    owner_account_id: '',
    status: ''
  });
  
  const [tempFilters, setTempFilters] = useState({ ...filters });
  const [tempSummaryFilters, setTempSummaryFilters] = useState({ ...summaryFilters });
  
  const { isMobile, isTablet, isDesktop } = useResponsive();
  
  // Theme colors based on sidebar history (teal/cyan)
  const theme = {
    primary: 'from-teal-500 to-cyan-500',
    primaryLight: 'from-teal-400 to-cyan-400',
    secondary: 'from-teal-500 to-cyan-500',
    bg: 'bg-teal-100 dark:bg-teal-900/30',
    text: 'text-teal-600 dark:text-teal-400',
    border: 'border-teal-200 dark:border-teal-800',
    ring: 'ring-teal-500',
    hover: 'hover:bg-teal-50 dark:hover:bg-teal-900/20',
    gradient: 'from-teal-500 to-cyan-500',
    lightBg: 'bg-teal-50 dark:bg-teal-900/20',
    badge: {
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      refunded: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      processing: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
    }
  };
  
  const toLocalDateTime = (date) => {
    const tzOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - tzOffset);
    return localDate.toISOString().slice(0, 19);
  };

  const getTodayRange = () => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return {
      from: toLocalDateTime(todayStart),
      to: toLocalDateTime(todayEnd)
    };
  };

  const parseFilterDateTime = (dateStr) => {
    if (!dateStr) return null;
    const normalized = dateStr.length === 16 ? `${dateStr}:00` : dateStr;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getQuickRangeDateTimes = (range) => {
    const now = new Date();
    let fromDate;
    let toDate;

    if (range === 'today') {
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    } else if (range === 'yesterday') {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      fromDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0);
      toDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59);
    } else if (range === 'this_week') {
      const day = now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((day + 6) % 7));
      fromDate = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0);
      toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    } else if (range === 'this_month') {
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    } else {
      return null;
    }

    return {
      from: toLocalDateTime(fromDate),
      to: toLocalDateTime(toDate)
    };
  };

  const detectQuickRange = (fromDateTime, toDateTime) => {
    const ranges = ['today', 'yesterday', 'this_week', 'this_month'];
    return ranges.find((range) => {
      const quickRangeValues = getQuickRangeDateTimes(range);
      return quickRangeValues?.from === fromDateTime && quickRangeValues?.to === toDateTime;
    }) || '';
  };

  const filterHistoryItems = (items = [], activeFilters = {}) => {
    const fromDate = parseFilterDateTime(activeFilters.from_datetime);
    const toDate = parseFilterDateTime(activeFilters.to_datetime);
    const statusFilter = (activeFilters.status || '').trim().toLowerCase();
    const ownerFilter = String(activeFilters.owner_account_id || '').trim().toLowerCase();

    return items.filter((item) => {
      const itemDate = parseFilterDateTime(item.created_at || item.updated_at || item.order_date);
      if (fromDate && (!itemDate || itemDate < fromDate)) return false;
      if (toDate && (!itemDate || itemDate > toDate)) return false;

      if (statusFilter) {
        const itemStatus = String(item.status || '').trim().toLowerCase();
        if (itemStatus !== statusFilter) return false;
      }

      if (ownerFilter) {
        const itemOwner = String(item.owner_account_id ?? item.user_id ?? '').trim().toLowerCase();
        if (itemOwner !== ownerFilter) return false;
      }

      return true;
    });
  };
  
  const getStatusMeta = (row) => {
    const total = parseInt(row.total_count || 0);
    const success = parseInt(row.success_count || 0);
    const failed = parseInt(row.failed_count || 0);
    const pending = parseInt(row.pending_count || 0);
    const refunded = parseInt(row.refunded_count || 0);
    const status = row.status;

    if (status === 'refunded') {
      return { icon: '♻️', label: 'Refunded', style: theme.badge.refunded };
    }
    if (status === 'success') {
      return { icon: '✅', label: 'Success', style: theme.badge.success };
    }
    if (status === 'failed') {
      return { icon: '❌', label: 'Failed', style: theme.badge.failed };
    }
    if (status === 'pending') {
      return { icon: '⌛', label: 'Pending', style: theme.badge.pending };
    }

    if (total > 0 && refunded === total) {
      return { icon: '♻️', label: 'Refunded', style: theme.badge.refunded };
    }
    if (total > 0 && success === total) {
      return { icon: '✅', label: 'Success', style: theme.badge.success };
    }
    if (total > 0 && pending === total) {
      return { icon: '⌛', label: 'Pending', style: theme.badge.pending };
    }
    if (total > 0 && failed === total) {
      return { icon: '❌', label: 'Failed', style: theme.badge.failed };
    }
    if (success > 0 && (failed > 0 || pending > 0 || refunded > 0)) {
      return { icon: '⚠️', label: 'Partial', style: theme.badge.partial };
    }
    if (pending > 0) {
      return { icon: '⚙️', label: 'Processing', style: theme.badge.processing };
    }
    return { icon: '❔', label: 'Unknown', style: theme.badge.pending };
  };
  
  const resolveAmountValue = (...candidates) => {
    for (const candidate of candidates) {
      const parsed = parseFloat(candidate);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        return parsed;
      }
    }
    return 0;
  };

  const getAmountCandidates = (row = {}) => ({
    price: [
      row.real_amount,
      row.charged_amount,
      row.amount,
      row.price,
      row.user_sale,
      row.total_amount
    ],
    cost: [
      row.real_admin_coin_amount,
      row.admin_coin_amount,
      row.cost,
      row.admin_amount,
      row.coin_price,
      row.reseller_price,
      row.cost_amount,
      row.total_cost
    ]
  });

  const getRoleMmkAmount = (row = {}) => {
    if (userRole === 'reseller') {
      return resolveAmountValue(
        row.reseller_sale,
        row.real_amount,
        row.charged_amount,
        row.amount,
        row.price
      );
    }

    return resolveAmountValue(
      row.user_sale,
      row.real_amount,
      row.charged_amount,
      row.amount,
      row.price
    );
  };

  const getDisplayAmounts = (row) => {
    const isSystemOrSuperAdmin = userRole === 'system' || userRole === 'super_admin';
    const isOwnTransaction = currentUserId && parseInt(row.owner_account_id) === currentUserId;
    const amountCandidates = getAmountCandidates(row);
    const realAmount = resolveAmountValue(...amountCandidates.price);
    const chargedCurrency = row.charged_currency || row.currency;
    const price = {
      amount: realAmount.toFixed(2),
      currency: chargedCurrency || 'MMK'
    };

    const realAdminCoinAmount = resolveAmountValue(...amountCandidates.cost);
    const adminCurrency = row.admin_currency || row.charged_currency || row.currency;
    const rolePrice = isSystemOrSuperAdmin
      ? price
      : { amount: getRoleMmkAmount(row).toFixed(2), currency: 'MMK' };

    let cost = null;
    if (isSystemOrSuperAdmin) {
      if (isOwnTransaction) {
        cost = price;
      } else {
        cost = { amount: realAdminCoinAmount.toFixed(2), currency: adminCurrency || 'MMK' };
      }
    }

    return { cost, price: rolePrice };
  };
  
  const getHistoryDisplayAmounts = (history) => {
    const isSystemOrSuperAdmin = userRole === 'system' || userRole === 'super_admin';
    const isOwnTransaction = currentUserId && parseInt(history.owner_account_id) === currentUserId;
    const amountCandidates = getAmountCandidates(history);
    const realAmount = resolveAmountValue(...amountCandidates.price);
    const chargedCurrency = history.charged_currency || history.currency;
    const price = {
      amount: realAmount.toFixed(2),
      currency: chargedCurrency || 'MMK'
    };

    const realAdminCoinAmount = resolveAmountValue(...amountCandidates.cost);
    const adminCurrency = history.admin_currency || history.charged_currency || history.currency;
    const rolePrice = isSystemOrSuperAdmin
      ? price
      : { amount: getRoleMmkAmount(history).toFixed(2), currency: 'MMK' };

    let cost = null;
    if (isSystemOrSuperAdmin) {
      if (isOwnTransaction) {
        cost = price;
      } else {
        cost = { amount: realAdminCoinAmount.toFixed(2), currency: adminCurrency || 'MMK' };
      }
    }

    return { cost, price: rolePrice };
  };

  const getHistoryQuantity = (history) => {
    const quantity = Number(
      history?.quantity ??
      history?.total_count ??
      history?.count ??
      history?.item_count ??
      history?.request_fields?.quantity ??
      1
    );

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };
  
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await accountAPI.getMe();
        if (response.data.success) {
          setUserRole(response.data.data.role);
          setCurrentUserId(response.data.data.id);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    fetchUserInfo();
  }, []);
  
  // IMPORTANT FIX: List API doesn't accept filters, so we fetch all and filter client-side
  const fetchHistory = useCallback(async (customFilters = null, pageOverride = null) => {
    setLoading(true);
    try {
      const activeFilters = customFilters || filters;
      const page = pageOverride ?? pagination.page;
      const params = {
        page,
        page_size: pagination.pageSize,
        ...activeFilters
      };
      
      const response = await historyAPI.listGroups(params);
      
      if (response.data && response.data.success) {
        const historyPayload = response.data.data;
        const normalizedHistory = Array.isArray(historyPayload)
          ? historyPayload
          : Array.isArray(historyPayload?.data)
            ? historyPayload.data
            : Array.isArray(historyPayload?.items)
              ? historyPayload.items
              : [];
        const filteredHistory = filterHistoryItems(normalizedHistory, activeFilters);

        setHistoryData(prev => (page > 1 ? [...prev, ...filteredHistory] : filteredHistory));
        setPagination(prev => ({
          ...prev,
          page,
          total: page > 1
            ? prev.total + filteredHistory.length
            : filteredHistory.length,
          hasMore: Boolean(response.data.has_more) && normalizedHistory.length > 0
        }));
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to fetch history');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);
  
  // Fetch summary data
  const fetchSummary = useCallback(async (customFilters = null) => {
    setSummaryLoading(true);
    try {
      const activeFilters = customFilters || summaryFilters;
      const params = {};
      if (activeFilters.from_datetime) params.from_datetime = activeFilters.from_datetime;
      if (activeFilters.to_datetime) params.to_datetime = activeFilters.to_datetime;
      if (activeFilters.owner_account_id) params.owner_account_id = activeFilters.owner_account_id;
      if (activeFilters.status) params.status = activeFilters.status;
      
      const response = await historyAPI.checkSummary(params);
      
      if (response.data && response.data.success) {
        setSummaryData(response.data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryFilters]);
  
  useEffect(() => {
    if (currentUserId) {
      const todayRange = getTodayRange();
      const initialFilters = {
        from_datetime: todayRange.from,
        to_datetime: todayRange.to,
        owner_account_id: '',
        status: ''
      };
      setFilters(initialFilters);
      setTempFilters(initialFilters);
      setQuickRange('today');
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchHistory(initialFilters, 1);
      fetchSummary(initialFilters);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId || pagination.page <= 1) return;
    fetchHistory(filters, pagination.page);
  }, [currentUserId, pagination.page, fetchHistory, filters]);

  useEffect(() => {
    if (!showFilters) return;
    const handleClickOutside = (event) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  useEffect(() => {
    if (!(showGroupModal || showHistoryModal)) return undefined;

    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyTouchAction = document.body.style.touchAction;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.touchAction = originalBodyTouchAction;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [showGroupModal, showHistoryModal]);
  
  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setQuickRange(detectQuickRange(tempFilters.from_datetime, tempFilters.to_datetime));
    setPagination(prev => ({ ...prev, page: 1 }));
    setHasAppliedFilters(true);
    fetchHistory(tempFilters, 1);
    setShowFilters(false);
    
    const summaryFilterParams = {
      from_datetime: tempFilters.from_datetime,
      to_datetime: tempFilters.to_datetime,
      owner_account_id: tempFilters.owner_account_id,
      status: tempFilters.status
    };
    setSummaryFilters(summaryFilterParams);
    fetchSummary(summaryFilterParams);
  };
  
  const handleSummarySearch = () => {
    setSummaryFilters({ ...tempSummaryFilters });
    fetchSummary(tempSummaryFilters);
    setShowFilters(false);
    const historyFilters = {
      from_datetime: tempSummaryFilters.from_datetime,
      to_datetime: tempSummaryFilters.to_datetime,
      owner_account_id: tempSummaryFilters.owner_account_id || '',
      status: tempSummaryFilters.status
    };
    setFilters(historyFilters);
    setTempFilters(historyFilters);
    setQuickRange(detectQuickRange(historyFilters.from_datetime, historyFilters.to_datetime));
    setPagination(prev => ({ ...prev, page: 1 }));
    setHasAppliedFilters(true);
    fetchHistory(historyFilters, 1);
  };
  
  const resetFilters = () => {
    const todayRange = getTodayRange();
    const emptyFilters = {
      from_datetime: todayRange.from,
      to_datetime: todayRange.to,
      owner_account_id: '',
      status: ''
    };
    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setQuickRange('today');
    setPagination(prev => ({ ...prev, page: 1 }));
    setHasAppliedFilters(false);
    fetchHistory(emptyFilters, 1);
    
    const summaryFilterParams = {
      from_datetime: todayRange.from,
      to_datetime: todayRange.to,
      owner_account_id: '',
      status: ''
    };
    setTempSummaryFilters(summaryFilterParams);
    setSummaryFilters(summaryFilterParams);
    fetchSummary(summaryFilterParams);
  };

  const applyQuickRange = (range) => {
    const quickRangeValues = getQuickRangeDateTimes(range) || getTodayRange();
    const { from, to } = quickRangeValues;

    setQuickRange(range);

    const updatedFilters = {
      ...filters,
      from_datetime: from,
      to_datetime: to
    };

    setShowFilters(false);
    setTempFilters(updatedFilters);
    setFilters(updatedFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    setHasAppliedFilters(true);
    fetchHistory(updatedFilters, 1);

    const updatedSummaryFilters = {
      ...summaryFilters,
      from_datetime: from,
      to_datetime: to
    };
    setTempSummaryFilters(updatedSummaryFilters);
    setSummaryFilters(updatedSummaryFilters);
    fetchSummary(updatedSummaryFilters);
  };

  const handleTempFilterChange = (field, value) => {
    const updatedFilters = {
      ...tempFilters,
      [field]: value
    };

    setTempFilters(updatedFilters);

    if (field === 'from_datetime' || field === 'to_datetime') {
      setQuickRange(detectQuickRange(updatedFilters.from_datetime, updatedFilters.to_datetime));
    }
  };

  const viewGroupDetails = async (orderGroupId) => {
    setModalLoading(true);
    try {
      const response = await historyAPI.getGroup(orderGroupId);
      if (response.data && response.data.success) {
        const payload = response.data.data;
        if (Array.isArray(payload)) {
          setSelectedGroup(payload);
        } else if (Array.isArray(payload?.items)) {
          setSelectedGroup(payload.items);
        } else if (Array.isArray(payload?.data)) {
          setSelectedGroup(payload.data);
        } else {
          setSelectedGroup(payload ? [payload] : []);
        }
        setShowGroupModal(true);
      } else {
        toast.error('Order details not found');
      }
    } catch (error) {
      console.error('Error fetching group:', error);
      toast.error('Failed to fetch group details');
    } finally {
      setModalLoading(false);
    }
  };
  
  const viewHistoryDetails = async (historyId) => {
    setModalLoading(true);
    try {
      const response = await historyAPI.getHistory(historyId);
      if (response.data && response.data.success) {
        setSelectedHistory(response.data.data);
        setShowHistoryModal(true);
      } else {
        toast.error('Transaction details not found');
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to fetch history details');
    } finally {
      setModalLoading(false);
    }
  };
  
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Copied to clipboard');
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return String(dateStr)
        .replace(/^(mon|tue|wed|thu|fri|sat|sun)\w*,?\s+/i, '')
        .replace('T', ' ')
        .replace(/\.\d+/, '')
        .replace(/\s?(Z|GMT|UTC)$/i, '')
        .replace(/([+-]\d{2}:\d{2})$/, '')
        .trim();
    } catch {
      return dateStr;
    }
  };
  
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      return String(dateStr)
        .replace(/^(mon|tue|wed|thu|fri|sat|sun)\w*,?\s+/i, '')
        .replace('T', ' ')
        .replace(/\.\d+/, '')
        .replace(/\s?(Z|GMT|UTC)$/i, '')
        .replace(/([+-]\d{2}:\d{2})$/, '')
        .trim()
        .slice(0, 16)
        .replace(' ', 'T');
    } catch {
      return '';
    }
  };

  const getFilteredGroupItems = (groupData) => {
    if (!groupData) return [];

    let items = [];
    if (Array.isArray(groupData)) {
      items = groupData;
    } else if (Array.isArray(groupData.items)) {
      items = groupData.items;
    } else if (Array.isArray(groupData.data)) {
      items = groupData.data;
    }

    return filterHistoryItems(items, filters);
  };

  const getUserDisplayName = (history) => {
    if (history.request_fields?.display_name) {
      return history.request_fields.display_name;
    }
    if (history.request_fields?.name) {
      return history.request_fields.name;
    }
    if (history.chat_owner_name) {
      return history.chat_owner_name;
    }
    if (history.username) {
      return history.username;
    }
    if (history.owner_name) {
      return history.owner_name;
    }
    if (history.user_name) {
      return history.user_name;
    }
    if (history.external_order_id) {
      return history.external_order_id;
    }
    if (history.owner_role === 'reseller') {
      return history.owner_account_id ? `Reseller ${history.owner_account_id}` : 'Reseller';
    }
    if (history.owner_role === 'system') {
      return 'System Admin';
    }
    if (history.owner_role === 'super_admin') {
      return 'Super Admin';
    }
    return 'N/A';
  };

  const getGameIdValue = (history) => (
    history?.request_fields?.game_id ||
    history?.request_fields?.player_id ||
    history?.game_id ||
    '-'
  );

  const getServerIdValue = (history) => (
    history?.request_fields?.server_id ||
    history?.server_id ||
    '-'
  );

  const getReceiptFieldValue = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' && value.trim() === '') return fallback;
    return value;
  };

  const getGameUsername = (history) => (
    history?.username ||
    history?.response_payload?.response?.username ||
    history?.response_payload?.username ||
    history?.game_username ||
    '-'
  );

  const getDetailedItemLabel = (history) => (
    history?.based_item_name ||
    history?.based_item_code ||
    history?.custom_item_name ||
    history?.custom_item_code ||
    'Unnamed item'
  );

  const getRegionDisplayValue = (region) => {
    if (region === null || region === undefined) return '-';
    if (typeof region === 'string') {
      const trimmed = region.trim();
      if (!trimmed) return '-';
      if (trimmed === '1') return '-';
      return trimmed;
    }
    return String(region);
  };

  const formatFieldLabel = (key) => key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const getVisibleRequestEntries = (fields) => {
    if (!fields || typeof fields !== 'object') return [];

    const hiddenKeys = new Set([
      'status',
      'order_group_id',
      'order_id',
      'history_id',
      'transaction_status',
      'game_id',
      'server_id'
    ]);

    return Object.entries(fields).filter(([key, value]) => {
      if (hiddenKeys.has(key)) return false;
      if (value === null || value === undefined || value === '') return false;
      return true;
    });
  };

  const getAggregateStatusMeta = (items = []) => {
    if (!items.length) {
      return { icon: '•', label: 'No Items', style: theme.badge.pending };
    }

    const labelCounts = items.reduce((accumulator, item) => {
      const label = getStatusMeta(item).label;
      accumulator[label] = (accumulator[label] || 0) + 1;
      return accumulator;
    }, {});

    const labels = Object.keys(labelCounts);
    if (labels.length === 1) {
      return getStatusMeta(items[0]);
    }

    if (labelCounts.Failed && labelCounts.Success) {
      return { icon: '⚠️', label: 'Mixed Result', style: theme.badge.partial };
    }

    if (labelCounts.Processing || labelCounts.Pending) {
      return { icon: '⚙️', label: 'In Progress', style: theme.badge.processing };
    }

    return { icon: '⚠️', label: 'Mixed', style: theme.badge.partial };
  };

  const getCustomerId = (history) => (
    history?.chat_id ||
    history?.charged_account_id ||
    history?.customer_id ||
    history?.user_id ||
    history?.account_id ||
    history?.request_fields?.game_id ||
    history?.request_fields?.player_id ||
    '-'
  );

  const getGroupTotals = (items = []) => {
    const firstItem = items[0] || {};
    const totalPrice = resolveAmountValue(
      firstItem.charged_amount,
      firstItem.real_amount,
      firstItem.amount,
      firstItem.price,
      firstItem.user_sale,
      firstItem.total_amount
    );

    const totalCost = items.reduce((accumulator, item) => {
      const displayAmounts = getHistoryDisplayAmounts(item);
      const costValue = displayAmounts.cost ? parseFloat(displayAmounts.cost.amount || 0) : 0;
      return accumulator + costValue;
    }, 0);

    return {
      price: totalPrice,
      cost: totalCost
    };
  };

  const calculateSummaryStats = () => {
    if (!summaryData) return null;
    
    const totals = summaryData.totals || {};
    const meta = summaryData.meta || {};
    const products = summaryData.products || [];
    
    const totalCost = Object.values(totals.costs || {}).reduce((sum, cost) => sum + parseFloat(cost), 0);
    const userRevenue = parseFloat(totals.user_sale || 0);
    const resellerRevenue = parseFloat(totals.reseller_sale || 0);
    const totalOrders = meta.matched_rows || 0;
    const matchedQuantities = meta.matched_quantities || 0;
    const costs = totals.costs || {};
    
    let uniqueUsers = 0;
    if (historyData.length > 0) {
      const users = new Set(historyData.map(g => g.chat_owner_name).filter(Boolean));
      uniqueUsers = users.size;
    }
    
    return {
      totalOrders,
      userRevenue: userRevenue.toFixed(2),
      resellerRevenue: resellerRevenue.toFixed(2),
      totalCost: totalCost.toFixed(2),
      costs,
      matchedQuantities,
      uniqueUsers,
      currency: 'MMK'
    };
  };
  
  const allHistories = historyData.flat();
  const selectedGroupItems = getFilteredGroupItems(selectedGroup);
  const selectedGroupMeta = selectedGroupItems?.length > 0
    ? selectedGroupItems[0]
    : (Array.isArray(selectedGroup) ? selectedGroup[0] : selectedGroup) || {};
  const selectedGroupTotals = getGroupTotals(selectedGroupItems);
  const selectedGroupStatus = getAggregateStatusMeta(selectedGroupItems);
  const selectedGroupUser = getUserDisplayName(selectedGroupMeta);
  const selectedHistoryAmounts = selectedHistory ? getHistoryDisplayAmounts(selectedHistory) : null;
  const selectedHistoryStatus = selectedHistory ? getStatusMeta(selectedHistory) : null;
  const selectedHistoryUser = selectedHistory ? getUserDisplayName(selectedHistory) : null;
  const isSystemOrSuperAdmin = userRole === 'system' || userRole === 'super_admin';
  const hasActiveFilters = hasAppliedFilters || Object.values(filters).some(v => v && v !== filters.from_datetime && v !== filters.to_datetime);
  
  const summaryStats = calculateSummaryStats();

  // Modern Stat Card Component
  const StatCard = ({ title, value, icon: Icon, color = 'teal', delay = 0 }) => {
    const colorClasses = {
      teal: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
      green: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      red: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
    };

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay }}
        className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </motion.div>
    );
  };

  // Modern Status Badge Component
  const StatusBadge = ({ statusMeta }) => {
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium ${statusMeta.style}`}>
        <span className="mr-1">{statusMeta.icon}</span>
        {statusMeta.label}
      </span>
    );
  };

  // Modern History Card for Mobile
  const HistoryCard = ({ history }) => {
    const statusMeta = getStatusMeta(history);
    const displayAmounts = getDisplayAmounts(history);
    const userDisplay = getUserDisplayName(history);
    const quantity = getHistoryQuantity(history);

    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ y: -2 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-gray-700"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${theme.bg} flex items-center justify-center`}>
              <FiShoppingBag className={`w-4 h-4 ${theme.text}`} />
            </div>
            <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
              #{history.order_id || history.id || '-'}
            </span>
          </div>
          <StatusBadge statusMeta={statusMeta} />
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Package</p>
              <p className="mt-1 text-gray-700 dark:text-gray-300">{history.custom_item_name || history.custom_item_code || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Display Name</p>
              <p className="mt-1 text-gray-700 dark:text-gray-300">{userDisplay}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Game ID</p>
              <p className="mt-1 text-gray-700 dark:text-gray-300">{getGameIdValue(history)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Server ID</p>
              <p className="mt-1 text-gray-700 dark:text-gray-300">{getServerIdValue(history)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Quantity</p>
              <p className="mt-1 text-gray-700 dark:text-gray-300">{quantity}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">Created At</p>
              <p className="mt-1 text-gray-700 dark:text-gray-300">{formatDateTime(history.created_at)}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">Price</p>
              <p className="mt-1 text-sm font-medium text-teal-600 dark:text-teal-400">
                {displayAmounts.price.amount} {displayAmounts.price.currency}
              </p>
              {displayAmounts.cost ? (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Cost: {displayAmounts.cost.amount} {displayAmounts.cost.currency}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <button
          onClick={() => viewGroupDetails(history.order_group_id)}
          className="mt-4 w-full py-2.5 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors text-sm font-medium"
        >
          View Details
        </button>
      </motion.div>
    );
  };

  // Modern History Table Row for Desktop
  const HistoryTableRow = ({ history }) => {
    const statusMeta = getStatusMeta(history);
    const displayAmounts = getDisplayAmounts(history);
    const userDisplay = getUserDisplayName(history);
    const quantity = getHistoryQuantity(history);

    return (
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => viewGroupDetails(history.order_group_id)}
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
            #{history.order_id || history.id || '-'}
          </span>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <span className="text-sm text-gray-900 dark:text-white">{history.custom_item_name || history.custom_item_code || '-'}</span>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <span className="text-sm text-gray-900 dark:text-white">{userDisplay}</span>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          {getGameIdValue(history)}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          {getServerIdValue(history)}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          {quantity}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <StatusBadge statusMeta={statusMeta} />
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          {formatDateTime(history.created_at)}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <span className="text-sm font-medium text-teal-600 dark:text-teal-400">
            {displayAmounts.price.amount} {displayAmounts.price.currency}
          </span>
          {displayAmounts.cost ? (
            <span className="block text-xs text-red-600 dark:text-red-400">
              Cost: {displayAmounts.cost.amount} {displayAmounts.cost.currency}
            </span>
          ) : null}
        </td>
      </motion.tr>
    );
  };

  // Modern Modal Shell Component
  const ModalShell = ({ icon: Icon, title, subtitle, onClose, children, footer }) => {
    return (
      <div className="fixed inset-0 z-[80] overflow-hidden">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative flex min-h-full items-end justify-center sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-2xl"
          >
            <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5 flex-shrink-0`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{title}</h3>
                    <p className="text-sm text-white/80 mt-0.5">{subtitle}</p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <FiX className="w-5 h-5 text-white" />
                </motion.button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-hide p-6 bg-gray-50 dark:bg-gray-900">
              {children}
            </div>

            {footer && (
              <div className="border-t border-gray-100 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800 flex-shrink-0">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    );
  };

  // Modern Mini Stat Component
  const MiniStat = ({ label, value, subvalue = null, tone = 'default' }) => {
    const toneStyles = tone === 'accent'
      ? 'border-teal-200 bg-teal-50/90 dark:border-teal-800/80 dark:bg-teal-950/30'
      : tone === 'danger'
        ? 'border-red-200 bg-red-50/90 dark:border-red-900/70 dark:bg-red-950/30'
        : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900/80';

    return (
      <div className={`rounded-xl border px-4 py-3 shadow-sm ${toneStyles}`}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p className="mt-2 break-words text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
          {getReceiptFieldValue(value)}
        </p>
        {subvalue && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {subvalue}
          </p>
        )}
      </div>
    );
  };

  // Modern Meta Chip Component
  const MetaChip = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-800 dark:bg-gray-900/80">
      <Icon className={`h-4 w-4 flex-shrink-0 ${theme.text}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">{label}</p>
        <p className="mt-1 break-words font-medium text-gray-900 dark:text-white">{getReceiptFieldValue(value)}</p>
      </div>
    </div>
  );

  // Modern Section Title Component
  const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-4">
      <h4 className="text-sm font-semibold uppercase tracking-[0.22em] text-gray-800 dark:text-gray-100">
        {title}
      </h4>
      {subtitle && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {subtitle}
        </p>
      )}
    </div>
  );

  if (loading && historyData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-teal-200 dark:border-teal-900 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-6 relative">
      {modalLoading && (
        <div className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-sm flex items-center justify-center">
          <div className="px-5 py-3 bg-white dark:bg-gray-900 rounded-xl shadow-xl flex items-center gap-2">
            <FiLoader className="w-5 h-5 animate-spin text-teal-500" />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-100">Loading order details...</span>
          </div>
        </div>
      )}

      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r ${theme.gradient} rounded-2xl p-6 sm:p-8 shadow-lg`}
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <FiClock className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Transaction History
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-teal-100 text-sm mt-1"
            >
              Track and manage all payment transactions
            </motion.p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-4 inline-flex"
        >
          <span className="px-4 py-2 rounded-xl text-xs font-medium bg-white/20 backdrop-blur-sm text-white">
            {userRole === 'system' ? 'System Administrator' :
             userRole === 'super_admin' ? 'Super Admin' :
             userRole === 'reseller' ? 'Reseller' : 'User'}
          </span>
        </motion.div>
      </motion.div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2"></div>
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
              </div>
            </div>
          ))
        ) : summaryStats ? (
          <>
            {isSystemOrSuperAdmin && (
              <StatCard 
                title="User Revenue" 
                value={`${summaryStats.userRevenue} MMK`} 
                icon={FiTrendingUp} 
                color="teal" 
                delay={0.1} 
              />
            )}
            {isSystemOrSuperAdmin && (
              <StatCard 
                title="Reseller Revenue" 
                value={`${summaryStats.resellerRevenue} MMK`} 
                icon={FiUsers} 
                color="purple" 
                delay={0.15} 
              />
            )}
            {summaryStats.costs && Object.entries(summaryStats.costs).slice(0, 2).map(([currency, amount], idx) => (
              <StatCard 
                key={currency}
                title={`${currency} Cost`} 
                value={`${parseFloat(amount).toFixed(2)} ${currency}`} 
                icon={FiDollarSign} 
                color="red" 
                delay={0.2 + idx * 0.05} 
              />
            ))}
            {Object.entries(summaryStats.costs || {}).length < 2 && (
              <StatCard 
                title="Total Orders" 
                value={summaryStats.totalOrders} 
                icon={FiPackage} 
                color="blue" 
                delay={0.2} 
              />
            )}
          </>
        ) : (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">No data</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">--</p>
                </div>
                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <FiBarChart2 className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Quick Range & Filters Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
      >
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Quick Range</span>
            {['today', 'yesterday', 'this_week', 'this_month'].map((range) => (
              <button
                key={range}
                onClick={() => applyQuickRange(range)}
                className={`px-3 py-1.5 rounded-lg text-sm transition ${quickRange === range ? `bg-teal-500 text-white` : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'}`}
              >
                {range === 'today' ? 'Today' : range === 'yesterday' ? 'Yesterday' : range === 'this_week' ? 'This Week' : 'This Month'}
              </button>
            ))}
            <button
              onClick={() => setShowFilters(true)}
              className={`p-2 rounded-lg transition ${showFilters ? 'bg-teal-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
              title="Advanced filters"
            >
              <FiFilter className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Search and Actions Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search transactions by user ID..."
              value={tempFilters.owner_account_id}
              onChange={(e) => setTempFilters({ ...tempFilters, owner_account_id: e.target.value })}
              className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={tempFilters.status}
              onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}
              className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>

            <button
              onClick={handleApplyFilters}
              className={`px-4 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all shadow-lg shadow-teal-500/20 flex items-center gap-2`}
            >
              <FiSearch className="w-5 h-5" />
              <span className="hidden sm:inline">Apply</span>
            </button>

            {!isMobile && (
              <div className="hidden sm:flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-white dark:bg-gray-600 text-teal-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                  title="Table view"
                >
                  <FiList className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 text-teal-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                  title="Grid view"
                >
                  <FiGrid className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filter info message */}
        {hasActiveFilters && (
          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <FiFilter className="w-4 h-4" />
            <span>Filters applied: </span>
            {filters.from_datetime && <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">From: {formatDateTime(filters.from_datetime)}</span>}
            {filters.to_datetime && <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">To: {formatDateTime(filters.to_datetime)}</span>}
            {filters.status && <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">Status: {filters.status}</span>}
            {filters.owner_account_id && <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">User: {filters.owner_account_id}</span>}
            <button onClick={resetFilters} className="text-teal-500 hover:text-teal-600 ml-2">Clear all</button>
          </div>
        )}
      </motion.div>

      {/* Transactions Display */}
      {historyData.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700"
        >
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/30 dark:to-cyan-900/30 rounded-full flex items-center justify-center mb-4">
            <FiClock className="w-12 h-12 text-teal-600 dark:text-teal-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Transactions Found</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {hasActiveFilters
              ? 'No transactions match your filters.'
              : 'No transactions for the selected period.'}
          </p>
          {hasActiveFilters && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={resetFilters}
              className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${theme.primary} text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-colors text-sm shadow-lg shadow-teal-500/20`}
            >
              <FiRefreshCw className="mr-2 w-4 h-4" />
              Clear filters
            </motion.button>
          )}
        </motion.div>
      ) : (
        <>
          {isMobile ? (
            <div className="space-y-4">
              <AnimatePresence>
                {allHistories.map((history) => (
                  <HistoryCard key={history.id} history={history} />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <AnimatePresence>
                    {allHistories.map((history) => (
                      <HistoryCard key={history.id} history={history} />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
                  <div className="overflow-x-auto scrollbar-hide">
                    <table className="w-full min-w-[1180px]">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Order ID</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Package</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Display Name</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Game ID</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Server ID</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quantity</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created At</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        <AnimatePresence>
                          {allHistories.map((history) => (
                            <HistoryTableRow key={history.id} history={history} />
                          ))}
                        </AnimatePresence>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Load More Button */}
      {pagination.hasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            className="px-6 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Load More
          </button>
        </div>
      )}

      {/* Filter Modal */}
      <AnimatePresence>
        {showFilters && (
          <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => setShowFilters(false)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5 flex-shrink-0`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <FiFilter className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Advanced Filters</h3>
                        <p className="text-sm text-teal-100">Filter transactions by various criteria</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowFilters(false)}
                      className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </motion.button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto scrollbar-hide p-6 bg-gray-50 dark:bg-gray-900">
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">From Date</label>
                      <input
                        type="datetime-local"
                        value={tempFilters.from_datetime}
                        onChange={(e) => handleTempFilterChange('from_datetime', e.target.value)}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">To Date</label>
                      <input
                        type="datetime-local"
                        value={tempFilters.to_datetime}
                        onChange={(e) => handleTempFilterChange('to_datetime', e.target.value)}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User ID</label>
                      <input
                        type="text"
                        value={tempFilters.owner_account_id}
                        onChange={(e) => setTempFilters({ ...tempFilters, owner_account_id: e.target.value })}
                        placeholder="Enter user ID"
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                      <select
                        value={tempFilters.status}
                        onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white"
                      >
                        <option value="">All Status</option>
                        <option value="success">Success</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <FiInfo className="inline mr-1 w-3 h-3" />
                      Filters are sent directly to the History and Check APIs so both sections stay in sync.
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 p-6 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={resetFilters}
                    className="px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                  >
                    Reset to Today
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleApplyFilters}
                    className={`px-6 py-2 bg-gradient-to-r ${theme.primary} text-white rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg shadow-teal-500/20`}
                  >
                    <FiSearch className="w-4 h-4" />
                    Apply Filters
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Order Details Modal */}
      <AnimatePresence>
        {showGroupModal && selectedGroup && (
          <ModalShell
            icon={FiFileText}
            title="Order Details"
            subtitle="Complete order summary with all items"
            onClose={() => setShowGroupModal(false)}
            footer={
              <div className="flex gap-3">
                <button
                  onClick={() => setShowGroupModal(false)}
                  className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white transition hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            }
          >
            <div className="space-y-6">
              {/* Order Summary Card */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-5">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">
                        Order ID
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <p className="break-all text-sm font-semibold text-gray-900 dark:text-white">
                          {getReceiptFieldValue(selectedGroupMeta.order_id || selectedGroupMeta.id)}
                        </p>
                        {(selectedGroupMeta.order_id || selectedGroupMeta.id) && (
                          <button
                            onClick={() => copyToClipboard(String(selectedGroupMeta.order_id || selectedGroupMeta.id), 'group-order')}
                            className="p-1.5 text-gray-400 hover:text-teal-600 transition-colors rounded-lg"
                          >
                            {copiedId === 'group-order' ? <FiCheck className="h-4 w-4 text-teal-500" /> : <FiCopy className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <MetaChip icon={FiUser} label="Customer" value={selectedGroupUser} />
                      <MetaChip icon={FiHash} label="Chat ID" value={getCustomerId(selectedGroupMeta)} />
                      <MetaChip icon={FiHash} label="Owner Account ID" value={selectedGroupMeta.owner_account_id} />
                      <MetaChip icon={FiClock} label="Ordered" value={formatDateTime(selectedGroupMeta.created_at || selectedGroupMeta.order_date)} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:min-w-[280px]">
                    <MiniStat label="Items" value={selectedGroupItems.length.toString()} subvalue="in this order" />
                    <MiniStat label="Status" value={selectedGroupStatus.label} subvalue={selectedGroupStatus.icon} tone="accent" />
                    <MiniStat label="Total Price" value={`${selectedGroupTotals.price.toFixed(2)} ${selectedGroupMeta.charged_currency || 'MMK'}`} tone="accent" />
                    {isSystemOrSuperAdmin && (
                      <MiniStat label="Total Cost" value={`${selectedGroupTotals.cost.toFixed(2)} ${selectedGroupMeta.admin_currency || selectedGroupMeta.charged_currency || 'MMK'}`} tone="danger" />
                    )}
                  </div>
                </div>
              </div>

              {/* Items List */}
              <div>
                <SectionTitle title="Order Items" subtitle="Tap one item to open its transaction details" />
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50">
                  {selectedGroupItems.map((history, index) => {
                    const statusMeta = getStatusMeta(history);

                    return (
                      <button
                        key={history.id}
                        onClick={() => { setShowGroupModal(false); viewHistoryDetails(history.id); }}
                        className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-teal-50 dark:hover:bg-gray-800 ${index !== selectedGroupItems.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''}`}
                      >
                        <div className="min-w-0 flex items-center gap-3">
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-600 text-xs font-semibold text-white">
                            {index + 1}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                              {getDetailedItemLabel(history)}
                            </p>
                            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                              Txn #{history.id}
                              {history.region && history.region !== '1' ? ` • ${history.region}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`inline-flex items-center rounded-lg px-2 py-0.5 text-[11px] font-medium ${statusMeta.style}`}>
                            {statusMeta.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>

      {/* Transaction Details Modal */}
      <AnimatePresence>
        {showHistoryModal && selectedHistory && (
          <ModalShell
            icon={FiList}
            title="Transaction Details"
            subtitle="Complete transaction information"
            onClose={() => setShowHistoryModal(false)}
            footer={
              <div className="flex gap-3">
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white transition hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            }
          >
            <div className="space-y-5">
              <div className="overflow-hidden rounded-[24px] border border-teal-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800/70">
                <div className="bg-gradient-to-r from-teal-50 via-white to-cyan-50 px-5 py-4 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-medium ${selectedHistoryStatus?.style || theme.badge.pending}`}>
                          {selectedHistoryStatus?.icon} {selectedHistoryStatus?.label}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">
                          Game Username
                        </p>
                        <p className="mt-1 break-words text-lg font-semibold text-gray-900 dark:text-white">
                          {getGameUsername(selectedHistory)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 lg:min-w-[360px]">
                      <MiniStat label="Amount" value={selectedHistoryAmounts ? `${selectedHistoryAmounts.price.amount} ${selectedHistoryAmounts.price.currency}` : '-'} tone="accent" />
                      {selectedHistoryAmounts?.cost ? (
                        <MiniStat label="Cost" value={`${selectedHistoryAmounts.cost.amount} ${selectedHistoryAmounts.cost.currency}`} tone="danger" />
                      ) : (
                        <MiniStat label="Status" value={selectedHistoryStatus ? selectedHistoryStatus.label : '-'} tone="accent" />
                      )}
                      <MiniStat label="Product" value={selectedHistory.product_name || selectedHistory.product_code || 'Unknown'} />
                      <MiniStat label="Package" value={getDetailedItemLabel(selectedHistory)} />
                      <MiniStat label="Quantity" value={getHistoryQuantity(selectedHistory)} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 border-t border-gray-100 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-800/50 sm:grid-cols-2 xl:grid-cols-4">
                  <MiniStat label="Game ID" value={selectedHistory.request_fields?.game_id} />
                  <MiniStat label="Server ID" value={selectedHistory.request_fields?.server_id} />
                  <MiniStat label="Region" value={getRegionDisplayValue(selectedHistory.region)} />
                  <MiniStat label="Transaction ID" value={selectedHistory.id} />
                </div>
              </div>

              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <div className="space-y-5">
                    {getVisibleRequestEntries(selectedHistory.request_fields).length > 0 && (
                      <div className="rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800/50">
                        <SectionTitle title="Game Details" subtitle="Additional values returned with this transaction" />
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {getVisibleRequestEntries(selectedHistory.request_fields).map(([key, value]) => (
                            <MiniStat key={key} label={formatFieldLabel(key)} value={String(value)} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800/50">
                  <SectionTitle title="Transaction Info" subtitle="Operational references for this order" />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <MiniStat label="Order ID" value={selectedHistory.order_id || selectedHistory.id} />
                    <MiniStat label="Product ID" value={selectedHistory.product_id} />
                    <MiniStat label="Custom Item ID" value={selectedHistory.custom_item_id} />
                    <MiniStat label="Updated At" value={formatDateTime(selectedHistory.updated_at || selectedHistory.created_at)} />
                  </div>
                </div>
              </div>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default History;
